












import Vue, { PropType } from 'vue';
import config from '@/config';
import ReviewsList, { IReview } from '@/components/ReviewsList/index.vue';
import gql from 'graphql-tag';

interface IBusinessReview {
  __typename: string;
  id: string;
  ts: number;
  reviewer: {
    __typename: string;
    id: string;
    fullName: string;
    displayPic?: string;
  };
  rating: number;
  comments?: string;
}

interface IFindBusinessByIDResult {
  findBusinessByID: {
    __typename: string;
    id: string;
    reviews: {
      __typename: string;
      data: IBusinessReview[];
      before?: string;
    };
  };
}

const reviewsConfig = config.business.reviews;
const reviewsSize = reviewsConfig.size;
const startingCursor = reviewsConfig.startingCursor;

export default Vue.extend({
  name: 'Reviews',
  props: {
    bizId: {
      type: String,
      required: true,
    },
    avgRating: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      reviews: [] as IBusinessReview[],
      nextCursor: undefined as undefined | null | string, // null explicitly means no next cursor
    };
  },
  computed: {
    transformedReviews(): IReview[] {
      const transformedReviews: IReview[] = this.reviews.map<IReview>(
        review => {
          const { reviewer, ...rest } = review;
          return {
            reviewerName: reviewer.fullName,
            ...rest,
          };
        }
      );

      return transformedReviews;
    },
    canShowMore(): boolean {
      return !!this.nextCursor;
    },
  },
  methods: {
    showMore(): void {
      this.$apollo.queries.reviews.fetchMore({
        variables: {
          id: this.bizId,
          size: reviewsSize,
          nextCursor: this.nextCursor,
        },
        updateQuery: (
          previousResult: IFindBusinessByIDResult,
          { fetchMoreResult }
        ): IFindBusinessByIDResult => {
          const oldReviews = previousResult.findBusinessByID.reviews;
          const newReviews = (fetchMoreResult as IFindBusinessByIDResult)
            .findBusinessByID.reviews;

          return {
            findBusinessByID: {
              __typename: previousResult.findBusinessByID.__typename,
              id: previousResult.findBusinessByID.id,
              reviews: {
                __typename: oldReviews.__typename,
                // merge data
                // new before old since we're getting data from last-one first
                // e.g. data will come in like[nth-1, nth] then.. [nth-3, nth-2] etc.
                data: [...newReviews.data, ...oldReviews.data],
                before: newReviews.before,
              },
            },
          };
        },
      });
    },
  },
  components: {
    ReviewsList,
  },
}).extend({
  apollo: {
    reviews: {
      query: gql`
        query getBusinessReviews(
          $businessID: ID!
          $size: Int
          $nextCursor: String
        ) {
          findBusinessByID(id: $businessID) {
            id: _id
            reviews(_size: $size, _cursor: $nextCursor) {
              data {
                id: _id
                ts: _ts
                reviewer {
                  fullName
                  displayPic
                }
                rating
                comments
              }
              before
            }
          }
        }
      `,
      variables() {
        return {
          businessID: this.bizId,
          nextCursor: startingCursor,
          size: reviewsSize,
        };
      },
      update(data: IFindBusinessByIDResult) {
        const result = data.findBusinessByID.reviews;
        this.nextCursor = result.before;

        return result.data;
      },
    },
  },
});
